// ReceiptDetail.tsx
import React, { useState } from 'react';
import { Modal, Button, Card, Row, Col, Typography, message as antMessage } from 'antd';
import { PrinterOutlined, WhatsAppOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { getApps, initializeApp } from 'firebase/app';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { uploadBytes } from '../utils/protectedStorage';
import { Receipt } from '../components/type';
import { usePatientContext } from './PatientContext';
import API from '../config/api';
import { useDoctorContext } from './DoctorContext';
import { applyWhatsAppLogo } from '../utils/whatsappBranding';

const { Title, Text } = Typography;

const _fbConfig = {
  apiKey: 'AIzaSyDJ8HyRAm7z0RlgmYy3EjubVujy7E3ZBfA',
  authDomain: 'drwaiting-30f56.firebaseapp.com',
  projectId: 'drwaiting-30f56',
  storageBucket: 'drwaiting-30f56.firebasestorage.app',
  messagingSenderId: '937005545176',
  appId: '1:937005545476:web:be56ec33fce7e64e9f49ad',
};
const _fbApp = getApps().find(a => a.name === '[DEFAULT]') || initializeApp(_fbConfig);
const _fbStorage = getStorage(_fbApp);

async function uploadHtmlForWA(html: string, path: string): Promise<string> {
  const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
  const fileRef = storageRef(_fbStorage, path);
  const snapshot = await uploadBytes(fileRef, blob, { contentType: 'text/html; charset=utf-8' });
  return getDownloadURL(snapshot.ref);
}

interface ReceiptDetailProps {
  onPrintReceipt: (receipt: Receipt) => void;
  onGetReceiptHtml?: (receipt: Receipt) => string;
}

const ReceiptDetail: React.FC<ReceiptDetailProps> = ({ onPrintReceipt, onGetReceiptHtml }) => {
  const [sending, setSending] = useState(false);
  const { settings: doctorSettings } = useDoctorContext();

  const { 
    selectedReceipt, 
    setSelectedReceipt, 
    isViewReceiptModalVisible, 
    setIsViewReceiptModalVisible,
    selectedPatient
  } = usePatientContext();

  const handleClose = () => {
    setIsViewReceiptModalVisible(false);
    setSelectedReceipt(null);
  };

  const handleSendWhatsApp = async () => {
    if (!selectedPatient?._id || !selectedReceipt?._id) return;
    const parentVisit = (selectedPatient as any).visits?.find((v: any) =>
      v?.receipts?.some((r: any) => r?._id === selectedReceipt._id)
    );
    const parentVisitId = parentVisit?._id || parentVisit?.visit_id;
    setSending(true);
    try {
      let firebaseUrl: string | undefined;
      if (onGetReceiptHtml) {
        try {
          const html = applyWhatsAppLogo(onGetReceiptHtml(selectedReceipt), doctorSettings);
          const path = `whatsapp/prescriptions/${selectedPatient._id}_${selectedReceipt._id}.html`;
          firebaseUrl = await uploadHtmlForWA(html, path);
        } catch (uploadErr: any) {
          console.warn('[WA] Firebase upload failed for receipt detail, backend will generate:', uploadErr?.message);
        }
      }
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_PRESCRIPTION(selectedPatient._id)}`,
        {
          receipt_id: selectedReceipt._id,
          ...(parentVisitId ? { visit_id: parentVisitId } : {}),
          ...(firebaseUrl ? { firebase_url: firebaseUrl } : {}),
        }
      );
      antMessage.success(`Prescription sent via WhatsApp to ${selectedPatient.patient_phone}`);
    } catch (err: any) {
      antMessage.error(err?.response?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Receipt Details"
      visible={isViewReceiptModalVisible}
      onCancel={handleClose}
      footer={[
        <Button
          key="whatsapp"
          type="primary"
          className="patient-whatsapp-button"
          icon={<WhatsAppOutlined />}
          loading={sending}
          disabled={!selectedPatient?.patient_phone}
          onClick={handleSendWhatsApp}
          style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
        >
          Send via WhatsApp
        </Button>,
        <Button 
          key="print" 
          icon={<PrinterOutlined />} 
          onClick={() => {
            if (!selectedReceipt) return;
            // Capture the receipt reference before closing the modal,
            // since handleClose() clears selectedReceipt via context.
            const receiptToPrint = selectedReceipt;
            // Close the Receipt Details modal BEFORE printing. On
            // Samsung tablets the print service snapshots the viewport
            // for its on-screen preview, so if the modal is still open
            // the preview shows "Receipt Details" behind the paper.
            // printHtml() waits 400 ms before print(), enough time for
            // the modal's exit animation to finish.
            handleClose();
            onPrintReceipt(receiptToPrint);
          }}
        >
          Print Receipt
        </Button>,
        <Button 
          key="close" 
          onClick={handleClose}
        >
          Close
        </Button>
      ]}
      width={700}
      style={{ direction: 'rtl', textAlign: 'right' }}
    >
      {selectedReceipt && (
        <div>
          <Title level={5}>Date: {moment(selectedReceipt.date).format('YYYY-MM-DD')}</Title>
          
          <Title level={5} style={{ marginTop: '16px' }}>Medications:</Title>
          {selectedReceipt.drugs.map((drug, index) => (
            <Card key={drug._id || index} size="small" style={{ marginBottom: '8px' }}>
              <Row gutter={[16, 8]}>
                <Col span={24}><Text strong>Drug: </Text><Text>{drug.drug}</Text></Col>
                <Col span={6}><Text strong>Frequency: </Text><Text>{drug.frequency}</Text></Col>
                <Col span={6}><Text strong>Period: </Text><Text>{drug.period}</Text></Col>
                <Col span={6}><Text strong>Timing: </Text><Text>{drug.timing}</Text></Col>
                <Col span={6}>
                  <Text strong>Dose qty: </Text>
                  <Text>{drug.doseQty && drug.doseQty !== '_' ? drug.doseQty : '—'}</Text>
                </Col>
              </Row>
            </Card>
          ))}
          
          {selectedReceipt.notes && (
            <>
              <Title level={5} style={{ marginTop: '16px' }}>Notes:</Title>
              <Text>{selectedReceipt.notes}</Text>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ReceiptDetail;
